import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { 
  FileText, 
  Plus, 
  Edit, 
  Trash2, 
  Eye, 
  Code,
  Copy,
  CheckCircle,
  XCircle
} from 'lucide-react';
import api from '@/services/api';
import { useApp } from '@/context/AppContext';
import { toast } from 'sonner';

const DOCUMENT_TYPES = [
  { value: 'autorisation', label: 'Autorisation de conduite' },
  { value: 'formation', label: 'Attestation de formation' },
  { value: 'certificat', label: 'Certificat' }
];

const DEFAULT_VARIABLES = [
  '{{nom}}',
  '{{prenom}}',
  '{{matricule}}',
  '{{date}}',
  '{{date_fin}}',
  '{{categories}}',
  '{{numero_autorisation}}',
  '{{secteur}}',
  '{{poste}}',
  '{{qr_code}}',
  '{{verification_url}}',
  '{{verification_code}}',
  // Variables spécifiques aux formations
  '{{intitule_formation}}',
  '{{date_formation}}',
  '{{organisme}}',
  '{{numero_attestation}}'
];

const DEFAULT_TEMPLATE = `<!DOCTYPE html>
<html>
<head>
  <style>
    body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; }
    .header { text-align: center; border-bottom: 3px solid #1e40af; padding-bottom: 20px; margin-bottom: 30px; }
    .header h1 { color: #1e40af; margin: 0; font-size: 28px; }
    .header p { color: #6b7280; margin-top: 10px; font-size: 16px; }
    .content { margin-top: 30px; }
    .field { margin-bottom: 15px; display: flex; }
    .label { font-weight: bold; color: #374151; min-width: 200px; }
    .value { color: #111827; }
    .qr-section { margin-top: 40px; text-align: center; padding: 20px; background: #f3f4f6; border-radius: 12px; }
    .qr-section h3 { margin-bottom: 15px; color: #374151; }
    .qr-code { margin: 20px auto; }
    .verification-info { margin-top: 15px; font-size: 14px; color: #6b7280; }
    .verification-code { font-family: monospace; font-size: 18px; font-weight: bold; color: #1e40af; background: #dbeafe; padding: 8px 16px; border-radius: 6px; display: inline-block; margin-top: 10px; }
    .footer { margin-top: 40px; text-align: center; font-size: 12px; color: #9ca3af; border-top: 1px solid #e5e7eb; padding-top: 20px; }
  </style>
</head>
<body>
  <div class="header">
    <h1>AUTORISATION DE CONDUITE</h1>
    <p>N° {{numero_autorisation}}</p>
  </div>
  <div class="content">
    <div class="field">
      <span class="label">Nom :</span>
      <span class="value">{{nom}}</span>
    </div>
    <div class="field">
      <span class="label">Prénom :</span>
      <span class="value">{{prenom}}</span>
    </div>
    <div class="field">
      <span class="label">Matricule :</span>
      <span class="value">{{matricule}}</span>
    </div>
    <div class="field">
      <span class="label">Secteur :</span>
      <span class="value">{{secteur}}</span>
    </div>
    <div class="field">
      <span class="label">Poste :</span>
      <span class="value">{{poste}}</span>
    </div>
    <div class="field">
      <span class="label">Catégories CACES :</span>
      <span class="value">{{categories}}</span>
    </div>
    <div class="field">
      <span class="label">Date de délivrance :</span>
      <span class="value">{{date}}</span>
    </div>
    <div class="field">
      <span class="label">Date de fin de validité :</span>
      <span class="value">{{date_fin}}</span>
    </div>
  </div>
  <div class="qr-section">
    <h3>🔐 Vérification d'authenticité</h3>
    <div class="qr-code">{{qr_code}}</div>
    <div class="verification-info">
      Scannez ce QR code ou utilisez le code ci-dessous pour vérifier l'authenticité
    </div>
    <div class="verification-code">{{verification_code}}</div>
  </div>
  <div class="footer">
    Document généré électroniquement - JELD-WEN Eauze<br/>
    Vérification en ligne : {{verification_url}}
  </div>
</body>
</html>`;

export default function GestionModeles() {
  const { currentUser } = useApp();
  const [modeles, setModeles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [typeFilter, setTypeFilter] = useState('all');
  
  const [dialogOpen, setDialogOpen] = useState(false);
  const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedModele, setSelectedModele] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');
  
  const [formData, setFormData] = useState({
    code: '',
    nom: '',
    type: 'autorisation',
    description: '',
    contenu_html: DEFAULT_TEMPLATE,
    variables: DEFAULT_VARIABLES
  });

  useEffect(() => {
    loadModeles();
  }, []);

  const loadModeles = async () => {
    try {
      setLoading(true);
      const data = await api.getModelesDocuments();
      setModeles(data);
    } catch (error) {
      toast.error('Erreur lors du chargement des modèles');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (selectedModele) {
        await api.updateModeleDocument(selectedModele.id, {
          nom: formData.nom,
          description: formData.description,
          contenu_html: formData.contenu_html,
          variables: formData.variables
        });
        toast.success('Modèle mis à jour');
      } else {
        await api.createModeleDocument(formData, currentUser.id, `${currentUser.prenom} ${currentUser.nom}`);
        toast.success('Modèle créé');
      }
      setDialogOpen(false);
      resetForm();
      loadModeles();
    } catch (error) {
      toast.error(error.message || 'Erreur lors de l\'enregistrement');
    }
  };

  const handleDelete = async () => {
    if (!selectedModele) return;
    try {
      await api.deleteModeleDocument(selectedModele.id);
      toast.success('Modèle supprimé');
      loadModeles();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    } finally {
      setDeleteDialogOpen(false);
      setSelectedModele(null);
    }
  };

  const handlePreview = async (modele) => {
    try {
      const sampleData = {
        nom: 'Dupont',
        prenom: 'Jean',
        matricule: 'MAT-2024-001',
        date: new Date().toLocaleDateString('fr-FR'),
        date_fin: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toLocaleDateString('fr-FR'),
        categories: 'R489-1A, R489-3',
        numero_autorisation: 'AUTH-2024-001',
        secteur: 'Logistique',
        poste: 'Cariste',
        qr_code: '<img src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAoAAAAKCAYAAACNMs+9AAAAFUlEQVR42mNk+M9Qz0AEYBxVSF+FABJADq2pSAuYAAAAAElFTkSuQmCC" alt="QR Code" style="width:150px;height:150px;background:#e5e7eb;display:block;margin:10px auto;border-radius:8px;"/>',
        verification_url: 'https://example.com/verify/autorisation/ABC123XYZ',
        verification_code: 'ABC123XYZ'
      };
      
      const result = await api.previewModeleDocument(modele.id, sampleData);
      setPreviewHtml(result.html);
      setPreviewDialogOpen(true);
    } catch (error) {
      toast.error('Erreur lors de la prévisualisation');
    }
  };

  // Generate live preview from template
  const generateLivePreview = (template) => {
    const sampleData = {
      nom: 'Dupont',
      prenom: 'Jean',
      matricule: 'JW-2024-001',
      date: new Date().toLocaleDateString('fr-FR'),
      date_fin: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toLocaleDateString('fr-FR'),
      categories: 'R489-1A, R489-3',
      numero_autorisation: 'AUTH-2024-001',
      secteur: 'Production',
      poste: 'Cariste',
      qr_code: '<div style="width:120px;height:120px;background:linear-gradient(45deg, #1e40af 25%, #3b82f6 25%, #3b82f6 50%, #1e40af 50%, #1e40af 75%, #3b82f6 75%);background-size:20px 20px;border-radius:8px;margin:0 auto;display:flex;align-items:center;justify-content:center;color:white;font-size:10px;text-align:center;">QR Code<br/>Preview</div>',
      verification_url: 'https://app.jeldwen.com/verify/autorisation/ABC123XYZ',
      verification_code: 'ABC123XYZ',
      // Variables spécifiques aux formations
      intitule_formation: 'Sécurité Incendie',
      date_formation: new Date().toLocaleDateString('fr-FR'),
      organisme: 'Formation Pro SARL',
      numero_attestation: 'FORM-2024-001'
    };
    
    let html = template;
    Object.entries(sampleData).forEach(([key, value]) => {
      html = html.replace(new RegExp(`\\{\\{${key}\\}\\}`, 'g'), value);
    });
    return html;
  };

  const openEditDialog = (modele) => {
    setSelectedModele(modele);
    setFormData({
      code: modele.code,
      nom: modele.nom,
      type: modele.type,
      description: modele.description || '',
      contenu_html: modele.contenu_html,
      variables: modele.variables || []
    });
    setDialogOpen(true);
  };

  const resetForm = () => {
    setSelectedModele(null);
    setFormData({
      code: '',
      nom: '',
      type: 'autorisation',
      description: '',
      contenu_html: DEFAULT_TEMPLATE,
      variables: DEFAULT_VARIABLES
    });
  };

  const copyVariable = (variable) => {
    navigator.clipboard.writeText(variable);
    toast.success(`Variable ${variable} copiée`);
  };

  const filteredModeles = modeles.filter(m => 
    typeFilter === 'all' || m.type === typeFilter
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gestion des Modèles</h1>
          <p className="text-gray-500">Modèles de documents pour les autorisations et attestations</p>
        </div>
        <Button onClick={() => { resetForm(); setDialogOpen(true); }} className="gap-2">
          <Plus className="h-4 w-4" />
          Nouveau modèle
        </Button>
      </div>

      {/* Filter */}
      <Card>
        <CardContent className="p-4">
          <div className="flex gap-4">
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="w-64">
                <SelectValue placeholder="Tous les types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Tous les types</SelectItem>
                {DOCUMENT_TYPES.map((type) => (
                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            </div>
          ) : filteredModeles.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <p className="text-gray-500">Aucun modèle trouvé</p>
              <Button variant="link" onClick={() => { resetForm(); setDialogOpen(true); }}>
                Créer un modèle
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Code</TableHead>
                  <TableHead>Nom</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Version</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Créé par</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredModeles.map((modele) => (
                  <TableRow key={modele.id}>
                    <TableCell>
                      <Badge variant="outline">{modele.code}</Badge>
                    </TableCell>
                    <TableCell className="font-medium">{modele.nom}</TableCell>
                    <TableCell>
                      {DOCUMENT_TYPES.find(t => t.value === modele.type)?.label || modele.type}
                    </TableCell>
                    <TableCell>v{modele.version}</TableCell>
                    <TableCell>
                      {modele.actif ? (
                        <Badge className="bg-green-100 text-green-800">
                          <CheckCircle className="h-3 w-3 mr-1" />Actif
                        </Badge>
                      ) : (
                        <Badge variant="secondary">
                          <XCircle className="h-3 w-3 mr-1" />Inactif
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>{modele.cree_par_nom || '-'}</TableCell>
                    <TableCell>
                      <div className="flex justify-end gap-2">
                        <Button variant="ghost" size="icon" onClick={() => handlePreview(modele)} title="Prévisualiser">
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="icon" onClick={() => openEditDialog(modele)} title="Modifier">
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => {
                            setSelectedModele(modele);
                            setDeleteDialogOpen(true);
                          }}
                          title="Supprimer"
                        >
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {selectedModele ? 'Modifier le modèle' : 'Nouveau modèle de document'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <Tabs defaultValue="general" className="w-full">
              <TabsList className="grid w-full grid-cols-3">
                <TabsTrigger value="general">Informations générales</TabsTrigger>
                <TabsTrigger value="template">Contenu HTML</TabsTrigger>
                <TabsTrigger value="preview">Aperçu en direct</TabsTrigger>
              </TabsList>
              
              <TabsContent value="general" className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Code *</Label>
                    <Input
                      value={formData.code}
                      onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                      placeholder="AUTH-STD"
                      required
                      disabled={!!selectedModele}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Type *</Label>
                    <Select 
                      value={formData.type} 
                      onValueChange={(v) => setFormData({ ...formData, type: v })}
                      disabled={!!selectedModele}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {DOCUMENT_TYPES.map((type) => (
                          <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                
                <div className="space-y-2">
                  <Label>Nom du modèle *</Label>
                  <Input
                    value={formData.nom}
                    onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                    placeholder="Autorisation de conduite standard"
                    required
                  />
                </div>
                
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Description du modèle..."
                    rows={3}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Variables disponibles</Label>
                  <div className="flex flex-wrap gap-2 p-3 bg-gray-50 rounded-lg">
                    {DEFAULT_VARIABLES.map((variable) => (
                      <Badge 
                        key={variable} 
                        variant="outline" 
                        className="cursor-pointer hover:bg-blue-50"
                        onClick={() => copyVariable(variable)}
                      >
                        <Code className="h-3 w-3 mr-1" />
                        {variable}
                        <Copy className="h-3 w-3 ml-1 opacity-50" />
                      </Badge>
                    ))}
                  </div>
                  <p className="text-xs text-gray-500">Cliquez sur une variable pour la copier</p>
                </div>
              </TabsContent>
              
              <TabsContent value="template" className="space-y-4 mt-4">
                <div className="space-y-2">
                  <Label>Contenu HTML du modèle</Label>
                  <Textarea
                    value={formData.contenu_html}
                    onChange={(e) => setFormData({ ...formData, contenu_html: e.target.value })}
                    placeholder="<!DOCTYPE html>..."
                    rows={20}
                    className="font-mono text-sm"
                    required
                  />
                </div>
              </TabsContent>

              <TabsContent value="preview" className="mt-4">
                <div className="space-y-4">
                  <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
                    <p className="text-sm text-blue-800">
                      <strong>Aperçu en temps réel</strong> - Les variables sont remplacées par des données d'exemple
                    </p>
                  </div>
                  <div className="border rounded-lg overflow-hidden shadow-lg bg-white">
                    <div className="bg-gray-100 px-4 py-2 border-b flex items-center gap-2">
                      <div className="flex gap-1">
                        <div className="w-3 h-3 rounded-full bg-red-400"></div>
                        <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                        <div className="w-3 h-3 rounded-full bg-green-400"></div>
                      </div>
                      <span className="text-xs text-gray-500 ml-2">Aperçu du document</span>
                    </div>
                    <iframe
                      srcDoc={generateLivePreview(formData.contenu_html)}
                      className="w-full h-[500px]"
                      title="Live Preview"
                    />
                  </div>
                </div>
              </TabsContent>
            </Tabs>

            <DialogFooter className="mt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Annuler
              </Button>
              <Button type="submit">
                {selectedModele ? 'Mettre à jour' : 'Créer'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={previewDialogOpen} onOpenChange={setPreviewDialogOpen}>
        <DialogContent className="max-w-4xl max-h-[90vh]">
          <DialogHeader>
            <DialogTitle>Prévisualisation du document</DialogTitle>
          </DialogHeader>
          <div className="border rounded-lg overflow-auto max-h-[70vh] bg-white">
            <iframe
              srcDoc={previewHtml}
              className="w-full h-[600px]"
              title="Document Preview"
            />
          </div>
          <DialogFooter>
            <Button onClick={() => setPreviewDialogOpen(false)}>Fermer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmer la suppression</AlertDialogTitle>
            <AlertDialogDescription>
              Êtes-vous sûr de vouloir supprimer le modèle <strong>{selectedModele?.nom}</strong> ?
              Cette action est irréversible.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Annuler</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} className="bg-red-600 hover:bg-red-700">
              Supprimer
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
